import { GraphQLResult } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';
import type {
  CreateReceiptUploadInput,
  CreateReimbursementTransactionInput,
  DeleteTransactionToReceiptUploadByReceiptIdInput,
  DeleteTransactionToReceiptUploadPayload,
  ExportReimbursementsInput,
  ExportReimbursementsPayload,
  GetReceiptUploadInput,
  GetReceiptsByTransactionIdInput,
  RenameReceiptUploadInput,
  GetReceiptsByTransactionIdPayload,
  GetReimbursementTransactionInput,
  GetReimbursementTransactionsByPaidolIdInput,
  GetReimbursementTransactionsByPaidolIdPayload,
  UpdateReimbursementTransactionInput,
  UpdateReimbursementsSettingsInput,
  V2ReimbursementTransaction,
  V2ReceiptUpload,
  BasicHttpResponse,
} from 'CDK_API';

// Re-export types used by other files
export type { BasicHttpResponse as BasicHTTPResponse } from 'CDK_API';
export type {
  CreateReceiptUploadInput,
  CreateReimbursementTransactionInput,
  DeleteTransactionToReceiptUploadByReceiptIdInput,
  DeleteTransactionToReceiptUploadPayload,
  ExportReimbursementsInput,
  ExportReimbursementsPayload,
  GetReceiptUploadInput,
  GetReceiptsByTransactionIdInput,
  RenameReceiptUploadInput,
  GetReceiptsByTransactionIdPayload,
  GetReimbursementTransactionInput,
  GetReimbursementTransactionsByPaidolIdInput,
  GetReimbursementTransactionsByPaidolIdPayload,
  UpdateReimbursementTransactionInput,
  UpdateReimbursementsSettingsInput,
  V2ReimbursementTransaction,
  V2ReceiptUpload,
} from 'CDK_API';

// --- GraphQL Operations ---

// Shared field selection used by all 4 large reimbursement-transaction operations.
// Represents the intersection of fields requested by get/getByPaidolId/create/update.
// Per-operation extras (checkrunStatus, contestNote, deniedNote, decisionDate, jobCode{},
// expenseType{}, receipt{}) are appended inline at each call site to preserve exact
// pre-refactor wire shape on each operation.
const REIMBURSEMENT_TRANSACTION_FIELDS = /* GraphQL */ `
  id
  paidolId
  authPaidolId
  transactionDate
  status
  note
  merchantName
  milesDriven
  purposeOfTrip
  mileageRate
  receiptLineItems
  amount {
    value
    currencyCode
  }
  jobCodeId
  reimbursementDate
  reviewedAt
  paidolUserId
  paidolUser {
    id
    user_id
    email
    roles
    user {
      id
      first_name
      last_name
      email
      picture
    }
  }
  transactionType
  expenseTypeId
  receiptId
  createdAt
  updatedAt
  dwollaTransferId
  agaveGlCodeId
  agaveGlCode {
    __typename
    id
    name
    number
    description
    fullName
  }
  agaveProjectId
  agaveProject {
    __typename
    id
    name
    number
    fullName
  }
  agaveCostCodeId
  agaveCostCode {
    __typename
    id
    name
    code
    full_code
    formatted_full_code
    fullName
  }
  agaveCostTypeId
  agaveCostType {
    __typename
    id
    name
    code
  }
  agaveVendorId
  agaveVendor {
    __typename
    id
    name
    code
    fullName
  }
  agaveSubJobId
  agaveSubJob {
    __typename
    id
    name
    number
    code
    fullName
  }
  agaveLocationId
  agaveLocation {
    __typename
    id
    name
    fullName
  }
  agaveDepartmentId
  agaveDepartment {
    __typename
    id
    name
    code
    fullName
  }
  agaveSubaccountId
  agaveSubaccount {
    __typename
    id
    name
    code
  }
  agaveEmployeeId
  agaveEmployee {
    __typename
    id
    name
    fullName
    firstName
    lastName
    email
  }
  agaveEquipmentId
  agaveEquipment {
    __typename
    id
    name
    code
    emGroup
    emCo
    # SEE-1435: department joins to the EM department that scopes the cost
    # code / cost type lists. Without it the scoping is silently inert.
    department
    fullName
  }
  agaveEmCostCodeId
  agaveEmCostCode {
    __typename
    id
    name
    code
    emGroup
    fullName
  }
  agaveEmCostTypeId
  agaveEmCostType {
    __typename
    id
    name
    code
    emGroup
    fullName
  }
  codingType
  accountingDate
  flexGlCodeId
  flexGlCode {
    id
    name
  }
  flexCodeId
  flexCode {
    id
    projectName
    projectCode
  }
  flexCostCodeId
  flexCostCode {
    id
    code
  }
  flexCostTypeId
  flexCostType {
    id
    name
  }
  flexVendorId
  flexVendor {
    id
    name
  }
  erpBillableItemId
  erpBillableItemName
`;

const getReimbursementTransactionQuery = /* GraphQL */ `
  query V2GetReimbursementTransaction($input: GetReimbursementTransactionInput!) {
    v2GetReimbursementTransaction(input: $input) {
      ${REIMBURSEMENT_TRANSACTION_FIELDS}
      checkrunStatus
      contestNote
      deniedNote
      decisionDate
      jobCode {
        id
        description
        code
      }
      expenseType {
        id
        name
      }
      receipt {
        id
        document
        status
        displayName
        sizeBytes
        createdAt
        updatedAt
      }
    }
  }
`;

const getReimbursementTransactionsByPaidolIdQuery = /* GraphQL */ `
  query V2GetReimbursementTransactionsByPaidolId($input: GetReimbursementTransactionsByPaidolIdInput!) {
    v2GetReimbursementTransactionsByPaidolId(input: $input) {
      items {
        ${REIMBURSEMENT_TRANSACTION_FIELDS}
        checkrunStatus
        contestNote
        deniedNote
        decisionDate
        jobCode {
          id
          description
          code
        }
        expenseType {
          id
          name
        }
      }
      nextToken
    }
  }
`;

const getReceiptUploadQuery = /* GraphQL */ `
  query V2GetReceiptUpload($input: GetReceiptUploadInput!) {
    v2GetReceiptUpload(input: $input) {
      id
      paidolId
      authPaidolId
      document
      status
      createdAt
      updatedAt
    }
  }
`;

const getReceiptsByTransactionIdQuery = /* GraphQL */ `
  query V2GetReceiptsByTransactionId($input: GetReceiptsByTransactionIdInput!) {
    v2GetReceiptsByTransactionId(input: $input) {
      items {
        id
        paidolId
        authPaidolId
        document
        status
        displayName
        sizeBytes
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const exportReimbursementsQuery = /* GraphQL */ `
  query V2ExportReimbursements($input: ExportReimbursementsInput!) {
    v2ExportReimbursements(input: $input) {
      csvContent
      filename
      success
      message
      exportedIds
      skippedIds
    }
  }
`;

const createReimbursementTransactionMutation = /* GraphQL */ `
  mutation V2CreateReimbursementTransaction($input: CreateReimbursementTransactionInput!) {
    v2CreateReimbursementTransaction(input: $input) {
      ${REIMBURSEMENT_TRANSACTION_FIELDS}
    }
  }
`;

const updateReimbursementTransactionMutation = /* GraphQL */ `
  mutation V2UpdateReimbursementTransaction($input: UpdateReimbursementTransactionInput!) {
    v2UpdateReimbursementTransaction(input: $input) {
      ${REIMBURSEMENT_TRANSACTION_FIELDS}
      checkrunStatus
      contestNote
      deniedNote
      decisionDate
    }
  }
`;

const createReceiptUploadMutation = /* GraphQL */ `
  mutation V2CreateReceiptUpload($input: CreateReceiptUploadInput!) {
    v2CreateReceiptUpload(input: $input) {
      id
      paidolId
      authPaidolId
      document
      status
      displayName
      sizeBytes
      createdAt
      updatedAt
    }
  }
`;

const renameReceiptUploadMutation = /* GraphQL */ `
  mutation V2RenameReceiptUpload($input: RenameReceiptUploadInput!) {
    v2RenameReceiptUpload(input: $input) {
      id
      paidolId
      authPaidolId
      document
      status
      displayName
      sizeBytes
      createdAt
      updatedAt
    }
  }
`;

const updateReimbursementsSettingsMutation = /* GraphQL */ `
  mutation V2UpdateReimbursementsSettings($input: UpdateReimbursementsSettingsInput!) {
    v2UpdateReimbursementsSettings(input: $input) {
      statusCode
      body
    }
  }
`;

const deleteTransactionToReceiptUploadMutation = /* GraphQL */ `
  mutation V2DeleteTransactionToReceiptUpload($input: DeleteTransactionToReceiptUploadByReceiptIdInput!) {
    v2DeleteTransactionToReceiptUpload(input: $input) {
      success
      message
    }
  }
`;

const v2ReimbursementsHealthCheckQuery = /* GraphQL */ `
  query V2ReimbursementsHealthCheck($input: String) {
    v2ReimbursementsHealthCheck(input: $input)
  }
`;

// --- Service ---

/**
 * Service for v2Reimbursements operations via the CDK AppSync API.
 *
 * All operations use Cognito auth (no IAM auth needed).
 * Follows the same pattern as UserService / PaidolService.
 */
export class ReimbursementsService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  async getReimbursementTransaction(
    input: GetReimbursementTransactionInput
  ): Promise<V2ReimbursementTransaction> {
    const result = await this.execute<{
      v2GetReimbursementTransaction: V2ReimbursementTransaction;
    }>(getReimbursementTransactionQuery, { input }, 'v2GetReimbursementTransaction');
    return result.v2GetReimbursementTransaction;
  }

  async createReimbursementTransaction(
    input: CreateReimbursementTransactionInput
  ): Promise<V2ReimbursementTransaction> {
    const result = await this.execute<{
      v2CreateReimbursementTransaction: V2ReimbursementTransaction;
    }>(createReimbursementTransactionMutation, { input }, 'v2CreateReimbursementTransaction');
    return result.v2CreateReimbursementTransaction;
  }

  async updateReimbursementTransaction(
    input: UpdateReimbursementTransactionInput
  ): Promise<V2ReimbursementTransaction> {
    const result = await this.execute<{
      v2UpdateReimbursementTransaction: V2ReimbursementTransaction;
    }>(updateReimbursementTransactionMutation, { input }, 'v2UpdateReimbursementTransaction');
    return result.v2UpdateReimbursementTransaction;
  }

  async getReceiptUpload(input: GetReceiptUploadInput): Promise<V2ReceiptUpload> {
    const result = await this.execute<{ v2GetReceiptUpload: V2ReceiptUpload }>(
      getReceiptUploadQuery,
      { input },
      'v2GetReceiptUpload'
    );
    return result.v2GetReceiptUpload;
  }

  async createReceiptUpload(input: CreateReceiptUploadInput): Promise<V2ReceiptUpload> {
    const result = await this.execute<{ v2CreateReceiptUpload: V2ReceiptUpload }>(
      createReceiptUploadMutation,
      { input },
      'v2CreateReceiptUpload'
    );
    return result.v2CreateReceiptUpload;
  }

  /** SEE-1598: change the name the receipt pane shows; the file and key are untouched. */
  async renameReceiptUpload(input: RenameReceiptUploadInput): Promise<V2ReceiptUpload> {
    const result = await this.execute<{ v2RenameReceiptUpload: V2ReceiptUpload }>(
      renameReceiptUploadMutation,
      { input },
      'v2RenameReceiptUpload'
    );
    return result.v2RenameReceiptUpload;
  }

  async getReimbursementTransactionsByPaidolId(
    input: GetReimbursementTransactionsByPaidolIdInput
  ): Promise<GetReimbursementTransactionsByPaidolIdPayload> {
    const result = await this.execute<{
      v2GetReimbursementTransactionsByPaidolId: GetReimbursementTransactionsByPaidolIdPayload;
    }>(getReimbursementTransactionsByPaidolIdQuery, { input }, 'v2GetReimbursementTransactionsByPaidolId');
    return result.v2GetReimbursementTransactionsByPaidolId;
  }

  async getReceiptsByTransactionId(
    input: GetReceiptsByTransactionIdInput
  ): Promise<GetReceiptsByTransactionIdPayload> {
    const result = await this.execute<{
      v2GetReceiptsByTransactionId: GetReceiptsByTransactionIdPayload;
    }>(getReceiptsByTransactionIdQuery, { input }, 'v2GetReceiptsByTransactionId');
    return result.v2GetReceiptsByTransactionId;
  }

  async exportReimbursements(input: ExportReimbursementsInput): Promise<ExportReimbursementsPayload> {
    const result = await this.execute<{
      v2ExportReimbursements: ExportReimbursementsPayload;
    }>(exportReimbursementsQuery, { input }, 'v2ExportReimbursements');
    return result.v2ExportReimbursements;
  }

  async updateReimbursementsSettings(input: UpdateReimbursementsSettingsInput): Promise<BasicHttpResponse> {
    const result = await this.execute<{
      v2UpdateReimbursementsSettings: BasicHttpResponse;
    }>(updateReimbursementsSettingsMutation, { input }, 'v2UpdateReimbursementsSettings');
    return result.v2UpdateReimbursementsSettings;
  }

  async deleteTransactionToReceiptUpload(
    input: DeleteTransactionToReceiptUploadByReceiptIdInput
  ): Promise<DeleteTransactionToReceiptUploadPayload> {
    const result = await this.execute<{
      v2DeleteTransactionToReceiptUpload: DeleteTransactionToReceiptUploadPayload;
    }>(deleteTransactionToReceiptUploadMutation, { input }, 'v2DeleteTransactionToReceiptUpload');
    return result.v2DeleteTransactionToReceiptUpload;
  }

  async v2ReimbursementsHealthCheck(input?: string): Promise<string> {
    const result = await this.execute<{ v2ReimbursementsHealthCheck: string }>(
      v2ReimbursementsHealthCheckQuery,
      { input: input ?? new Date().toISOString() },
      'v2ReimbursementsHealthCheck'
    );
    return result.v2ReimbursementsHealthCheck;
  }

  // --- Private helpers ---

  private async execute<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'reimbursements', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      const body = JSON.stringify({ query, variables });

      const response = await fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: jwt,
        },
        body,
      });

      const json = (await response.json()) as GraphQLResult<T> & {
        errors?: Array<{ message: string }>;
      };

      reportCdkRefusal(json?.errors, { authMode: 'cognito', operationName });

      if (!response.ok) {
        const message = json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const operationData = (json.data as Record<string, unknown> | null)?.[operationName];
        if (!json.data || operationData === null || operationData === undefined) {
          // Total failure — the operation returned nothing. This is how the
          // v2Reimbursements Lambda's { error } denials arrive (SEE-943): the
          // resolver raises them as GraphQL errors and the field comes back
          // null. Treating this as success made denials look like they worked.
          const errorMessage = json.errors.map((e) => e.message).join(', ');
          throw new Error(errorMessage);
        }
        // Partial errors — data is present but some fields had issues.
        // Log but don't throw, as the data is still usable.
        // eslint-disable-next-line no-console
        console.warn(`Partial GraphQL errors in ${operationName}:`, json.errors);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          // Transport failures (connection died in transit, already retried) are
          // tagged apart from HTTP/GraphQL rejections so they're separable in Sentry.
          tags: {
            area: 'reimbursements',
            type: isNetworkTransportError(error) ? 'network-transport' : 'graphql',
          },
          extra: { operation: operationName, endpoint: this.cdkAppSyncEndpoint },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }
}

// Export a singleton instance for convenience
export const reimbursementsService = new ReimbursementsService();
